'use client';

import { getBackgroundColor, getTextColor } from 'theme-utils';
import type {
  ThemeColorsBackground,
  ThemeColorsBorder,
  ThemeColorsText,
  ThemeRadii,
  ThemeSpaces,
} from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';

type Display =
  | 'block'
  | 'inline'
  | 'none'
  | 'inline-block'
  | 'flex'
  | 'inline-flex'
  | 'grid'
  | 'inline-grid';

type TextColor = ThemeColorsText;

type BackgroundColor = ThemeColorsBackground;

type AlignItems = 'flex-start' | 'center' | 'flex-end' | 'baseline';

type JustifyContent =
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'space-between'
  | 'space-around';

type FlexDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse';

type FlexWrap = 'wrap' | 'nowrap';

type FlexGrow = '0' | '1';

type FlexShrink = '0' | '1';

type FlexBasis = string;

type TextAlign = 'left' | 'center' | 'right';

type Height = '100%' | ThemeSpaces | string;

type Width = '100%' | ThemeSpaces | string;

type Position = 'absolute' | 'relative' | 'fixed';

type Overflow = 'hidden' | 'scroll' | 'visible' | 'auto';

type BorderBottom = boolean;

type BorderColor = ThemeColorsBorder;

type Sizes = {
  width?: Width;
  height?: Height;
  minWidth?: Width;
  minHeight?: Height;
  maxWidth?: Width;
  maxHeight?: Height;
};

type Positions = {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
};

type Paddings = {
  padding?: ThemeSpaces;
  paddingX?: ThemeSpaces;
  paddingY?: ThemeSpaces;
  paddingTop?: ThemeSpaces;
  paddingLeft?: ThemeSpaces;
  paddingRight?: ThemeSpaces;
  paddingBottom?: ThemeSpaces;
};

type Gap = ThemeSpaces;

export type BoxStyleOptions = {
  display?: Display;
  alignItems?: AlignItems;
  justifyContent?: JustifyContent;
  flexDirection?: FlexDirection;
  flexWrap?: FlexWrap;
  flexGrow?: FlexGrow;
  flexShrink?: FlexShrink;
  flexBasis?: FlexBasis;
  textAlign?: TextAlign;
  height?: Height;
  width?: Width;
  position?: Position;
  overflow?: Overflow;
  color?: TextColor;
  backgroundColor?: BackgroundColor;
  borderRadius?: ThemeRadii;
  transform?: string;
  /* borderBottom prop uses a box shadow to draw the border. */
  borderBottom?: BorderBottom;
  borderColor?: BorderColor;
  gap?: Gap;
} & Paddings &
  Positions &
  Sizes;

export const boxStyles = (props: BoxStyleOptions) => {
  const styles = (
    [
      'display',
      'position',
      'top',
      'right',
      'bottom',
      'left',
      'flexDirection',
      'flexShrink',
      'flexGrow',
      'flexWrap',
      'alignItems',
      'flexBasis',
      'justifyContent',
      'textAlign',
      'overflow',
      'transform',
    ] satisfies Array<keyof BoxStyleOptions>
  ).reduce((acc, key) => {
    // pick out the desired styles that are defined in the props
    // @ts-expect-error FIXME(noImplicitAny)
    if (Object.hasOwn(props, key)) acc[key] = props[key];
    return acc;
  }, {});

  return {
    ...styles,
    color: props.color ? getTextColor(props.color) : undefined,
    backgroundColor: props.backgroundColor
      ? getBackgroundColor(props.backgroundColor)
      : undefined,
    borderRadius: props.borderRadius
      ? theme.radii[props.borderRadius]
      : undefined,
    boxShadow:
      props.borderBottom && getBorder({ borderColor: props.borderColor }),
    ...getSizes(props),
    ...getPadding(props),
    gap: props.gap ? theme.spaces[props.gap] : undefined,
  };
};

const getBorder = ({ borderColor }: any) => {
  return `inset 0 -1px 0 0 ${borderColor || theme.colors.border.primary}`;
};

const getPadding = ({
  padding,
  paddingTop,
  paddingY,
  paddingBottom,
  paddingX,
  paddingLeft,
  paddingRight,
}: Paddings) => {
  const resolvedPaddingTop: ThemeSpaces | undefined =
    paddingTop || paddingY || padding;
  const resolvedPaddingBottom = paddingBottom || paddingY || padding;
  const resolvedPaddingLeft = paddingLeft || paddingX || padding;
  const resolvedPaddingRight = paddingRight || paddingX || padding;

  return {
    paddingLeft: resolvedPaddingLeft
      ? theme.spaces[resolvedPaddingLeft]
      : undefined,
    paddingTop: resolvedPaddingTop
      ? theme.spaces[resolvedPaddingTop]
      : undefined,
    paddingBottom: resolvedPaddingBottom
      ? theme.spaces[resolvedPaddingBottom]
      : undefined,
    paddingRight: resolvedPaddingRight
      ? theme.spaces[resolvedPaddingRight]
      : undefined,
  };
};

const getSizes = ({
  width,
  height,
  maxWidth,
  maxHeight,
  minWidth,
  minHeight,
}: Sizes) => {
  return {
    // @ts-expect-error FIXME(strictNullChecks)
    width: theme.spaces[width] ?? width,
    // @ts-expect-error FIXME(strictNullChecks)
    height: theme.spaces[height] ?? height,
    // @ts-expect-error FIXME(strictNullChecks)
    maxWidth: theme.spaces[maxWidth] ?? maxWidth,
    // @ts-expect-error FIXME(strictNullChecks)
    maxHeight: theme.spaces[maxHeight] ?? maxHeight,
    // @ts-expect-error FIXME(strictNullChecks)
    minWidth: theme.spaces[minWidth] ?? minWidth,
    // @ts-expect-error FIXME(strictNullChecks)
    minHeight: theme.spaces[minHeight] ?? minHeight,
  };
};
